import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { AreaView } from '../../../services/areas/areas.service';
import type { OrderView } from '../../../services/orders/orders.service';
import { orderlinesService } from '../../../services/orderlines/orderlines.service';
import { buildMinOrderCompletionNote, computeMinOrderReminderState } from '../components/orders/min-order/min-order.utils';

type PaymentOptions = { tipOnly?: boolean };

type PendingReminder = {
  orderId: number;
  options?: PaymentOptions;
  tableNumber: string;
  effectiveMinimum: number;
  currentTotal: number;
  missing: number;
  completionAmount: number;
};

type UseMinOrderReminderParams = {
  tenantId: string;
  employeeSession: string | null;
  activeAreas: AreaView[];
  activeOrders: OrderView[];
  proceed: (orderId: number, options?: PaymentOptions) => void;
};

export function useMinOrderReminder({
  tenantId,
  employeeSession,
  activeAreas,
  activeOrders,
  proceed,
}: UseMinOrderReminderParams) {
  const [reminder, setReminder] = useState<PendingReminder | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const inFlightRef = useRef(false);

  const requestOpenPayment = useCallback(
    async (orderId: number, options?: PaymentOptions) => {
      const order = activeOrders.find((entry) => entry.id === orderId);
      if (!order || order.orderType !== 'dine_in' || order.tableId === null || options?.tipOnly || !employeeSession) {
        proceed(orderId, options);
        return;
      }
      const table = activeAreas.flatMap((area) => area.tables).find((entry) => entry.id === order.tableId);
      const config = {
        minimumOrder: table?.minimumOrder ?? 0,
        minimumOrderPerDiner: table?.minimumOrderPerDiner ?? 0,
      };
      if (config.minimumOrder <= 0 && config.minimumOrderPerDiner <= 0) {
        proceed(orderId, options);
        return;
      }

      let lines;
      try {
        lines = await orderlinesService.listForOrder(tenantId, orderId, employeeSession);
      } catch {
        proceed(orderId, options);
        return;
      }
      const state = computeMinOrderReminderState(config, order.dinersCount, order.totalAmount ?? 0, lines);

      if (state.hasCompletionLine && state.completionAmount <= 0) {
        await orderlinesService
          .upsertMinOrderCompletion(tenantId, orderId, 0, buildMinOrderCompletionNote(state.effectiveMinimum), employeeSession)
          .catch(() => undefined);
      }

      if (state.missing <= 0) {
        proceed(orderId, options);
        return;
      }

      setReminder({
        orderId,
        options,
        tableNumber: order.tableNumber ?? '',
        effectiveMinimum: state.effectiveMinimum,
        currentTotal: state.currentTotal,
        missing: state.missing,
        completionAmount: state.completionAmount,
      });
    },
    [activeAreas, activeOrders, employeeSession, proceed, tenantId],
  );

  const continueWithout = useCallback(() => {
    if (!reminder) return;
    const { orderId, options } = reminder;
    setReminder(null);
    proceed(orderId, options);
  }, [reminder, proceed]);

  const dismiss = useCallback(() => setReminder(null), []);

  const addCompletion = useCallback(async () => {
    if (!reminder || !employeeSession || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsBusy(true);
    try {
      await orderlinesService.upsertMinOrderCompletion(
        tenantId,
        reminder.orderId,
        reminder.completionAmount,
        buildMinOrderCompletionNote(reminder.effectiveMinimum),
        employeeSession,
      );
    } catch {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'הוספת ההשלמה נכשלה', autoClose: 3000 });
      return;
    } finally {
      inFlightRef.current = false;
      setIsBusy(false);
    }
    const { orderId, options } = reminder;
    setReminder(null);
    proceed(orderId, options);
  }, [reminder, employeeSession, tenantId, proceed]);

  return { reminder, isBusy, requestOpenPayment, continueWithout, dismiss, addCompletion };
}
